﻿import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import {
  Typography,
  Container,
  TextField,
  Autocomplete,
  Checkbox,
  Grid,
  IconButton,
} from "@mui/material";
import "./GlobalSettings.css";
import {
  selectSelectedConnection,
} from "@/features/connections/integrationSlice";
import { useSelector } from "react-redux";
import axios from "@/features/axiosHelper";
import DeleteIcon from "@/assets/img/icons/Delete/Line.svg?react";
import ShepTable from "@/components/GenericComponents/ShepTable";

const EditQualifiersItems = forwardRef(
  ({ isTransactionSettings, selectedTransaction }, ref) => {
    const [options, setOptions] = useState([]);
    const [dataValues, setDataValues] = useState([]);
    const [showLookupMissing, setShowLookupMissing] = useState(false);
    const [autocompleteValue, setAutocompleteValue] = useState(null);
    const [autocompleteInputValue, setAutocompleteInputValue] = useState("");

    const selectedConnection = useSelector(selectSelectedConnection);

    const [artifactType, setArtifactType] = useState(-1); //To represent the global
    const [endpointSchemaId, setEndpointSchemaId] = useState(0); //To represent the global

    //setting artifactType and endpointSchemaId for transaction level
    useEffect(() => {
      if (isTransactionSettings) {
        setArtifactType(selectedTransaction[0].artifactType); //To represent the transaction set level
        setEndpointSchemaId(selectedTransaction[0].endpointSchemaId); //To represent the transaction set level
      }
    }, [isTransactionSettings, selectedTransaction]);

    useEffect(() => {
      if (selectedConnection.ConnectionId) {
        if (isTransactionSettings) {
          getFields(
            selectedConnection.ConnectionId,
            selectedTransaction[0].artifactType
          );
        } else if (!isTransactionSettings) {
          getFields(selectedConnection.ConnectionId, -1);
        }
      }
    }, [selectedConnection.ConnectionId, selectedTransaction]);

    const getFields = (connectionId, artifactType) => {
      axios
        .get("/connectionartifactschema/item-qualifiers", {
          params: {
            connectionId: connectionId,
            artifactType: artifactType,
          },
        })
        .then((response) => {
          setOptions(response.data.options);
          setDataValues(response.data.values);
        })
        .catch((error) => {
          console.error("Error fetching data: ", error);
        });
    };

    const columns = [
      {
        field: "Qualifier",
        headerName: "Qualifier Code",
        width: 250,
      },
      {
        field: "Description",
        headerName: "Description",
        width: 500,
      },
      {
        field: "IsLookup",
        headerName: "Is Lookup?",
        width: 180,
        renderCell: (params) => (
          <div>
            <Checkbox
              checked={params.row.IsLookup}
              id="IsLookup"
              value={params.row}
              name={params.row.Qualifier}
              onChange={handleToggle}
            />
          </div>
        ),
      },
      {
        field: "IsMandatory",
        headerName: "Mandatory Qualifier",
        description:
          "These qualifiers will be required when creating cross reference mappings from an EDI source.",
        width: 250,
        renderCell: (params) => (
          <div>
            <Checkbox
              checked={params.row.IsMandatory}
              id="IsMandatory"
              //disabled={!params.row.isLookup}
              value={params.row}
              name={params.row.Qualifier}
              onChange={handleToggle}
            />
          </div>
        ),
      },
      {
        field: "threeDotMenu",
        type: "actions",
        sortable: false,
        getActions: (params) => {
          return [
            <IconButton
              size="small"
              onClick={() => handleDeleteItemQualifier(params)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>,
          ];
        },
      },
    ];

    const getFormData = () => {
      return dataValues;
    };

    const validate = () => {
      const existsLookup = dataValues?.filter((x) => x.IsLookup);
      if (existsLookup?.length === 0 && dataValues?.length !== 0) {
        return "At least one qualifier must be lookup.";
      }
      return "";
    };

    useImperativeHandle(ref, () => ({
      getFormData,
      validate,
    }));

    const handleToggle = (e) => {
      const { name, checked, id } = e.target;
      const newDataValues = dataValues.map((x) => {
        if (x.Qualifier !== name) return x;
        else {
          if (id === "IsMandatory") return { ...x, IsMandatory: checked };
          if (id === "IsLookup") return { ...x, IsLookup: checked };
        }
      });

      setDataValues(newDataValues);
    };

    const handleDeleteItemQualifier = (params) => {
      const qualifier = params.row.Qualifier;
      const newDataValues = dataValues.filter((x) => x.Qualifier !== qualifier);
      setDataValues(newDataValues);
      const newOptions = options.map((e) => {
        if (e.Value !== qualifier) return e;
        else return { ...e, Disabled: false };
      });
      setOptions(newOptions);
    };

    const handleQualifierSelect = (e, v, r) => {
      if (!v) return;
      const newText = v.Text.slice(v.Value.length);
      const newListItem = {
        Qualifier: v.Value,
        Description: newText,
        IsLookup: false,
      };
      const newDataValues = dataValues.concat(newListItem);
      setDataValues(newDataValues);
      const newOptions = options.map((e) => {
        if (e.Value !== v.Value) return e;
        else return { ...e, Disabled: true };
      });
      setOptions(newOptions);
      setAutocompleteValue(null);
      setAutocompleteInputValue("");
    };

    const handleQualifierFilter = (options, { inputValue }) => {
      let filterResult = [];
      switch (inputValue.length) {
        case 0:
          return options;
        case 1:
          const matchStart = options.filter(
            (option) => option.Value.toLowerCase()[0] === inputValue
          );
          const matchEnd = options.filter(
            (option) =>
              option.Value.toLowerCase().includes(inputValue) &&
              !matchStart.includes(option)
          );
          filterResult = matchStart.concat(matchEnd);
          break;
        case 2:
          filterResult = options.filter(
            (option) => option.Value.toLowerCase() === inputValue
          );
          break;
        default:
          filterResult = options.filter((option) =>
            option.Text.toLowerCase().includes(inputValue)
          );
      }
      return filterResult;
    };

    return (
      <Container>
        <Grid
          container
          rowSpacing={1}
          columnSpacing={{ xs: 1, sm: 2, md: 3 }}
          sx={{
            justifyContent: "center",
          }}
        >
          <Grid item xs={1.5}></Grid>
          <Grid item xs={9}>
            <Typography variant="h2" sx={{ marginBottom: "20px" }}>
              Item Options Settings
            </Typography>
            <Autocomplete
              value={autocompleteValue}
              inputValue={autocompleteInputValue}
              onInputChange={(e, newInputValue) => {
                setAutocompleteInputValue(newInputValue);
              }}
              sx={{
                ".MuiInputBase-input": { height: "0px" },
              }}
              options={options}
              clearOnBlur
              style={{ width: 400 }}
              //style={{ width: "50%" }}
              getOptionLabel={(option) => {
                return option?.Text || "";
              }}
              getOptionDisabled={(option) => option.Disabled}
              isOptionEqualToValue={(option, value) => {
                return option?.Value === value.Value;
              }}
              onChange={handleQualifierSelect}
              filterOptions={handleQualifierFilter}
              slotProps={{
                popper: { sx: { zIndex: "1500" } },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                //label="Select Connection Pair"
                //placeholder="Select Connection Pair"
                />
              )}
            />
          </Grid>
          <Grid item xs={1.5}></Grid>

          <Grid item xs={1.5}></Grid>
          <Grid item xs={9}>
            <ShepTable
              dataRows={dataValues}
              columnTitles={columns}
              getRowId={(row) => row.Qualifier}
              selectionDisabled={true}
              emptyMessage={{
                title: "No Qualifiers Added",
                message: "Add a qualifier using the box above.",
              }}
            />

            {showLookupMissing && (
              <Typography color="red" variant="subtitle2">
                At least one qualifier needs to be lookup.
              </Typography>
            )}
          </Grid>
          <Grid item xs={1.5}></Grid>
        </Grid>
      </Container>
    );
  }
);

export default EditQualifiersItems;
